import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

import stringify from '../utils/stableStringify';
import { unpack, pack } from 'msgpackr';
import format from 'string-format';
import AntTime from '../utils/AntTime';
import AntConfig from '../utils/AntConfig';
import AntCondition from '../utils/AntCondition';
import AntLocalStorage from '../utils/AntLocalStorage';
import AntLocalization from '../utils/AntLocalization';
import { Template } from 'devextreme-react/core/template';
import { Validator } from 'devextreme-react/validator';
import { DropDownBox } from 'devextreme-react';
import DataGrid, { Selection, Paging, FilterRow, Scrolling, Grouping } from 'devextreme-react/data-grid';
import { CheckBox } from 'devextreme-react';
import AntQueryStore from './AntQueryStore';
import AntGridBar from './AntGridBar';
import AntString from '../utils/AntString';
import AntArray from '../utils/AntArray';
import AntGridCategories from './AntGridCategories';
import {
    buildGridSettingsKey,
    readGridSettings,
    updateGridSettings,
    mergeColumnSettings,
    getSavedColumnWidths,
    getSavedColumnVisibility,
    applyColumnOrder
} from '../utils/AntGridSettings';
import { resolveColumnFilterSpec, buildColumnFilterConditions, hasColumnFilterValues } from '../utils/AntGridColumnFilter';
import { prepareColumnFilterEditor } from '../utils/AntGridColumnFilterEditor';
import { attachHeaderBoundaryDblClick } from '../utils/AntGridHeaderDblClick';

import AntCategory from '../utils/AntCategory';

import { AntGlobal, AntSession } from '../utils/AntContext';

export default forwardRef(function (props, ref) {
    const appGlobal = AntGlobal();
    // const session = AntSession();

    if (props.API && !props.API.entityDisplay) {
        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
            let found = appGlobal.appConfig.Services.find((s) => s.name === props.API.entityName);
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    const tableName = props.gridOptions && props.gridOptions.table ? props.gridOptions.table : props.table ? props.table : props.API.entityName;

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    const CategoryData = useMemo(() => new AntCategory(appGlobal.appAssets.data.categoryData), []);

    const grid = useRef();
    const actionBar = useRef();
    // Chữ tìm kiếm giữ trong ref: đổi state làm DropDownBox dựng lại nội dung popup (gửi trùng request, mất focus).
    const searchTextRef = useRef('');

    // ---- Cấu hình người dùng của lưới trong ô chọn (AntGridSettings, khoá lookup:<tên editor>) ----
    // Lưu: độ rộng / ẩn hiện / thứ tự cột, sắp xếp, kích thước popup, bật dòng lọc. Giá trị đang lọc KHÔNG lưu
    // (mỗi lần mở là một lần chọn mới).
    const settingsName = String(props.settingsName || tableName + '.' + (props.dataField || props.name || '')).replace(/[#/]/g, '.');
    const settingsKey = buildGridSettingsKey('lookup:' + settingsName);
    const settingsMeta = useMemo(() => ({ scope: 'lookup', grid: settingsName, table: tableName }), [settingsName, tableName]);
    const savedSettings = useRef(null);
    if (savedSettings.current === null) savedSettings.current = readGridSettings(settingsKey);
    const userHidden = useRef(null);
    if (userHidden.current === null) {
        const vis = getSavedColumnVisibility(savedSettings.current);
        userHidden.current = {};
        Object.keys(vis).forEach((f) => {
            if (vis[f] === false) userHidden.current[f] = false;
        });
    }
    const userOrder = useRef(savedSettings.current.columnOrder instanceof Array ? savedSettings.current.columnOrder : null);
    const userSort = useRef(savedSettings.current.sort && savedSettings.current.sort.field ? savedSettings.current.sort : null);
    const filterRowVisible = useRef(Boolean(savedSettings.current.view && savedSettings.current.view.showFilterRow));
    const columnFilterValues = useRef({});
    const columnFilterTimer = useRef(null);
    const activeFilterEditor = useRef({ field: null, blurAt: 0 });
    // Thay đổi do chính mã (áp cấu hình, ẩn hiện theo điều kiện) — không coi là thao tác của người dùng để lưu.
    const applyingLayout = useRef(false);
    const saveSettings = useCallback((updater) => updateGridSettings(settingsKey, updater, settingsMeta), [settingsKey, settingsMeta]);

    const refUpdateTimeOut = useRef();
    const refLastChangedValue = useRef();

    const [mainValue, setMainValue] = useState(props.value);
    const refMainValue = useRef();

    const refByPassOnValueChangedEvent = useRef(false);

    const rawColumns = useMemo(
        () => (props.gridOptions && props.gridOptions.columns instanceof Array ? JSON.parse(stringify(props.gridOptions.columns)) : []),
        []
    );

    const onDataChanged = useCallback(
        (e) => {
            if (refLastChangedValue.current === undefined || e.value !== refLastChangedValue.current) {
                refLastChangedValue.current = e.value;
                if (props.onValueChanged) props.onValueChanged(e);
            }
        },
        [props.onValueChanged]
    );

    // const itemsCats1 = useRef([]);
    // Dùng useRef (KHÔNG useState): chọn category KHÔNG gây re-render AntDropdownGrid → DropDownBox
    // không dựng lại content → panel KHÔNG tự đóng. grid.refresh() đọc selectedCats1.current để lọc lại.
    // Lọc mặc định: searchCategories1.defaultValues (vd người bán → Nhà cung cấp); người dùng bấm đổi được.
    // Nút mặc định luôn hiện (AntGridCategories giữ mục đang chọn dù dữ liệu chưa có giá trị đó).
    const selectedCats1 = useRef(null);
    if (selectedCats1.current === null) {
        const cats = props.gridOptions && props.gridOptions.searchCategories1;
        const lookup = cats && cats.lookups instanceof Array && cats.lookups.length ? cats.lookups[0] : null;
        const defaults = cats && cats.defaultValues instanceof Array ? cats.defaultValues : [];
        const lookupName = lookup ? (typeof lookup === 'object' ? lookup.name || lookup.field : lookup) : null;
        const lookupField = lookup ? (typeof lookup === 'object' ? lookup.field || lookup.name : lookup) : null;
        let values = [];
        if (lookupField && config && config.getCnfField) {
            const cnf = config.getCnfField(lookupField, tableName);
            values = cnf && cnf.lookup && cnf.lookup.values instanceof Array ? cnf.lookup.values : [];
        }
        selectedCats1.current = lookupName
            ? defaults.map((value) => ({
                  name: lookupName,
                  field: lookupField,
                  value,
                  rawValue: values.find((v) => v && typeof v === 'object' && (v.code === value || v.value === value)) || value
              }))
            : [];
    }

    const getPageSize = useCallback(() => {
        if (props.defaultPageSize) return props.defaultPageSize;
        return 30;
    }, [props.defaultPageSize]);

    const initActionConditions = useCallback(() => {
        let ar = [];

        if (props.gridOptions && props.gridOptions.actionItems instanceof Array)
            props.gridOptions.actionItems.forEach((item) => {
                if (item.name)
                    ar.push({
                        name: item.name,
                        value: item.defaultValue !== undefined ? item.defaultValue : null
                    });
            });
        return ar;
    }, [props.gridOptions]);

    const [actionConditions, setActionConditions] = useState(() => initActionConditions());

    const getSearchConditions = useCallback(() => {
        let objS = {};

        if (props.gridOptions?.searchCategories1)
            objS = selectedCats1.current.reduce((obj, c) => {
                if (obj[c.field] !== undefined) obj[c.field].push(c.value);
                else obj[c.field] = [c.value];
                return obj;
            }, objS);

        Object.keys(objS).forEach((k) => {
            if (objS[k] instanceof Array && objS[k].length === 1) objS[k] = objS[k][0];
        });

        var obj = {
            lkTableName: props.lkTableName ? props.lkTableName : props.options && props.options.table ? props.options.table : props.table,
            lkName: !props.url && props.lookupName ? props.lookupName : undefined,
            ...objS
        };
        actionConditions.forEach((c) => {
            if (c.name && c.value !== undefined) obj[c.name] = c.value;
        });

        if (typeof props.searchConditions === 'object' && props.searchConditions !== null)
            Object.keys(props.searchConditions).forEach((k) => {
                if (props.searchConditions[k] !== undefined) obj[k] = props.searchConditions[k];
            });

        if (props.parentFields instanceof Array) {
            var formData = null;

            if (props.formRef && props.formRef.current && props.formRef.current.instance)
                formData = props.formRef.current.instance.option('formData');
            // Không có form (ô nhập trực tiếp trên lưới): giá trị lấy từ dòng/đối tượng cha.
            else if (props.parentData && typeof props.parentData === 'object') formData = props.parentData;

            if (formData) {
                props.parentFields.forEach((field) => {
                    if (typeof field === 'object' && field !== null) {
                        var parentField = field.field;
                        var aliasField = field.alias;
                        if (formData && formData[parentField] !== undefined && formData[parentField] !== null)
                            obj[aliasField] = formData[parentField];
                    } else if (formData && formData[field] !== undefined && formData[field] !== null) obj[field] = formData[field];
                });
            }
        }

        // Bộ lọc trên cột (dòng lọc đang hiện).
        if (filterRowVisible.current && hasColumnFilterValues(columnFilterValues.current)) {
            const instance = grid.current && grid.current.instance;
            const columnConditions = buildColumnFilterConditions(columnFilterValues.current, (field) => {
                const def = (mainColumnsRef.current || []).find((c) => c.dataField === field);
                if (!def || !def._antFilter) return null;
                if (instance && instance.columnOption(def.name || field, 'visible') === false) return null;
                return def._antFilter;
            });
            Object.assign(obj, columnConditions);
        }

        Object.keys(obj).forEach((key) => {
            if (obj[key] === undefined) {
                delete obj[key];
            }
        });

        obj.isSearching = props.isSearching !== undefined ? props.isSearching : true;

        return obj;
    }, [
        actionConditions,
        selectedCats1,
        props.gridOptions,
        props.lkTableName,
        props.options,
        props.url,
        props.lookupName,
        props.searchConditions,
        props.parentFields,
        props.parentData,
        props.formRef,
        props.formRef?.current,
        props.isSearching
    ]);

    const getQueryFields = useCallback(() => {
        var ar = ['created_at', 'checksum', 'id', 'code'];

        if (props.gridOptions && props.gridOptions.columns instanceof Array) {
            for (let i = 0; i < props.gridOptions.columns.length; i++) {
                const c = props.gridOptions.columns[i];

                var fieldName = null;
                if (typeof c === 'string') {
                    fieldName = String(c);
                } else if (typeof c === 'object' && c.dataField) fieldName = c.dataField;

                var col1 = rawColumns.find((c1) => c1.dataField === fieldName);
                if (col1 && (col1.colType === 'join' || col1.colType === 'first' || col1.colType === 'last') && col1.fields instanceof Array)
                    ar.push(...col1.fields);
                else ar.push(fieldName);
            }

            ar.push(
                ...Array.from(props.gridOptions.columns, (c) =>
                    typeof c === 'object' && c.defaultValue && typeof c.defaultValue === 'object' && c.defaultValue.rowField
                        ? c.defaultValue.rowField
                        : undefined
                )
            );
        }

        if (props.getQueryFields) {
            var fields = props.getQueryFields();
            if (fields instanceof Array) ar.push(...fields);
        }

        ar = ar
            .filter((v) => Boolean(v))
            .filter((v, index, self) => {
                return self.indexOf(v) === index;
            });

        return ar;
    }, [props.gridOptions, props.getQueryFields, rawColumns]);

    // Người dùng bấm tiêu đề để sắp xếp → ưu tiên; không thì thứ tự cấu hình.
    const getOrderings = useCallback(() => {
        if (userSort.current && userSort.current.field) return [{ name: userSort.current.field, type: userSort.current.order || 'asc' }];
        if (props.ordering instanceof Array) return props.ordering;
        return null;
    }, [props.ordering]);

    const onDatasourceLoading = useCallback((conditions) => {}, []);

    const onDatasourceLoaded = useCallback(
        (allRows, newRows) => {
            if (props.onLoaded) props.onLoaded(allRows, newRows);

            // resetUI();
        },
        [props.onLoaded]
    );

    const getSearchText = useCallback(() => searchTextRef.current, []);

    const initDataSource = useCallback(() => {
        let obj;

        if (props.isRemote) {
            obj = new AntQueryStore({
                key: props.gridOptions.valueExpr,
                API: props.API,
                url: props.url,
                loadFunc: 'postLookup',
                remotePaging: props.remotePaging !== undefined ? props.remotePaging : true,
                isScrolling: true,
                groupByField: props.groupByField,
                getPageSize: getPageSize,
                getSearchConditions: getSearchConditions,
                getQueryFields: getQueryFields,
                getOrderings: getOrderings,
                // getSearchText: getSearchText,
                onLoading: onDatasourceLoading,
                onLoaded: onDatasourceLoaded
            });
        }

        return obj;
    }, [
        props.isRemote,
        props.gridOptions,
        props.API,
        props.url,
        props.remotePaging,
        props.groupByField,
        getPageSize,
        getSearchConditions,
        getQueryFields,
        getOrderings,
        onDatasourceLoading,
        onDatasourceLoaded
    ]);

    const dataSource = useMemo(() => initDataSource(), [initDataSource]);

    const resetDataSource = useCallback(() => {
        if (dataSource)
            dataSource.reInit({
                key: dataSource.key,
                API: dataSource.API,
                url: dataSource.url,
                loadFunc: 'postLookup',
                remotePaging: dataSource.remotePaging,
                isScrolling: true,
                groupByField: dataSource.groupByField,
                getPageSize: getPageSize,
                getSearchConditions: getSearchConditions,
                getQueryFields: getQueryFields,
                getOrderings: getOrderings,
                getSearchText: getSearchText,
                onLoading: onDatasourceLoading,
                onLoaded: onDatasourceLoaded
            });
    }, [dataSource, getPageSize, getSearchConditions, getQueryFields, getOrderings, getSearchText, onDatasourceLoading, onDatasourceLoaded]);

    useEffect(() => {
        resetDataSource();
    }, [resetDataSource]);

    const initDropDownDataSource = useCallback(() => {
        var source = props.gridOptions.dataSource;
        var valueExpr = props.gridOptions.valueExpr;
        var displayExpr = props.gridOptions.displayExpr;

        var formData = null;
        if (props.formRef && props.formRef.current && props.formRef.current.instance) formData = props.formRef.current.instance.option('formData');

        var displayFormField = props.formDisplayField
            ? props.formDisplayField
            : props.dataField === 'code'
            ? 'name'
            : String(props.dataField).replace('_code', '_name');

        if (displayFormField) {
            if (props.isRemote) {
                source = dataSource.getCachedData();
                valueExpr = props.gridOptions.valueExpr;
                displayExpr = props.gridOptions.displayExpr;

                if (formData && formData[displayFormField] !== undefined && formData[props.dataField] !== undefined) {
                    if (source instanceof Array) {
                        var found = source.find((f) => f[valueExpr] === formData[props.dataField]);
                        if (!found) {
                            source.push({
                                [valueExpr]: formData[props.dataField],
                                [displayExpr]: formData[displayFormField]
                            });
                        }
                    }
                }
            } else {
                if (formData && formData[displayFormField] !== undefined) {
                    source = [formData];
                    valueExpr = props.dataField;
                    displayExpr = displayFormField;
                } else {
                    source = props.gridOptions.dataSource;
                    valueExpr = props.gridOptions.valueExpr;
                    displayExpr = props.gridOptions.displayExpr;
                }
            }
        }
        return {
            dataSource: source,
            valueExpr: valueExpr,
            displayExpr: displayExpr
        };
    }, [dataSource, props.gridOptions, props.dataField, props.formDisplayField, props.formRef, props.formRef?.current]);

    const dropdownDataSource = useMemo(() => initDropDownDataSource(), []);

    const initColumns = useCallback(() => {
        var formData = null;

        if (props.formRef && props.formRef.current && props.formRef.current.instance) formData = props.formRef.current.instance.option('formData');

        var columns = [];

        if (props.gridOptions && props.gridOptions.columns instanceof Array) {
            props.gridOptions.columns.forEach((col) => {
                if (typeof col === 'string') {
                    var dataField = String(col);
                    columns.push({ dataField: dataField, name: 'col' + dataField });
                } else if (typeof col === 'object' && col.dataField) {
                    var visible = true;

                    if (typeof col.visible === 'boolean') visible = col.visible;

                    if (visible) {
                        if (typeof col.visible === 'object') visible = AntCondition.isVisible(formData, col);
                        else if (col.config) visible = AntCondition.isVisible(formData, col.config);
                    }

                    if (visible) columns.push({ ...col, name: 'col' + col.dataField });
                }
            });
        }

        columns.forEach((col) => {
            var colType = col.dataType;
            var colEditor = col.editor;
            var colLabel = col.caption ? col.caption : col.label;

            if (colLabel instanceof Array) {
                colLabel = AntCondition.getValue(null, colLabel);
            }

            var colLookupValues = col.lookup && col.lookup.values instanceof Array ? col.lookup.values : null;

            var colWidth = col.width;
            var colMinWidth = col.minWidth;

            var colAllowHiding = col.allowHiding;
            var colAlignment = col.alignment;

            var found = null;
            if (config) {
                if (col.dataField || col.cnfField) {
                    var tableName = col.cnfTable
                        ? col.cnfTable
                        : props.gridOptions && props.gridOptions.table
                        ? props.gridOptions.table
                        : props.table
                        ? props.table
                        : props.API.entityName;

                    var strField = col.cnfField ? col.cnfField : col.dataField;
                    found = config.getCnfField(strField, tableName);
                    if (found) {
                        if (!colType && found.dataType) colType = found.dataType;
                        if (!colLabel && found.gridLabel) colLabel = found.gridLabel;
                        if (!colLabel && found.label) colLabel = found.label;
                        if (!colLookupValues && found.lookup && found.lookup.values instanceof Array) colLookupValues = found.lookup.values;

                        if (!colWidth && found.width) colWidth = found.width;
                        if (!colMinWidth && found.minWidth) colMinWidth = found.minWidth;
                        if (colAllowHiding === undefined && found.allowHiding !== undefined) colAllowHiding = found.allowHiding;

                        if (!colAlignment) {
                            if (found.dataType === 'currency') colAlignment = 'right';
                            else if (found.dataType === 'float' || found.dataType === 'decimal') colAlignment = 'center';
                            else if (found.dataType === 'int') colAlignment = 'center';
                            else colAlignment = found.alignment;
                        }
                    }

                    if (found && found.cnfTable) {
                        var tableName = found.cnfTable;

                        var strField = found.cnfField ? found.cnfField : col.dataField;
                        found = config.getCnfField(strField, tableName);
                    }
                    if (found) {
                        if (!colType && found.dataType) colType = found.dataType;
                        if (!colLabel && found.gridLabel) colLabel = found.gridLabel;
                        if (!colLabel && found.label) colLabel = found.label;
                        if (!colLookupValues && found.lookup && found.lookup.values instanceof Array) colLookupValues = found.lookup.values;

                        if (!colWidth && found.width) colWidth = found.width;
                        if (!colMinWidth && found.minWidth) colMinWidth = found.minWidth;
                        if (colAllowHiding === undefined && found.allowHiding !== undefined) colAllowHiding = found.allowHiding;

                        if (!colAlignment) {
                            if (found.dataType === 'currency') colAlignment = 'right';
                            else if (found.dataType === 'float' || found.dataType === 'decimal') colAlignment = 'center';
                            else if (found.dataType === 'int') colAlignment = 'center';
                            else colAlignment = found.alignment;
                        }

                        col.config = found;
                    }
                }
            }

            col.colType = colType;

            // if (!colWidth && colType === 'date') colWidth = 84;
            // if (!colWidth && colType === 'month') colWidth = 70;
            // if (!colWidth && colType === 'year') colWidth = 70;
            // if (!colWidth && colType === 'datetime') colWidth = 130;

            // if (!colWidth && String(col.dataField).endsWith('_status')) colWidth = 120;
            // if (!colWidth && String(col.dataField).endsWith('doc_code')) colWidth = 84;
            // if (!colWidth && String(col.dataField).endsWith('_code')) colWidth = 84;
            // if (!colWidth && String(col.dataField).endsWith('code')) colWidth = 84;
            // if (!colWidth && String(col.dataField).endsWith('module')) colWidth = 100;
            // if (!colWidth && String(col.dataField).endsWith('type')) colWidth = 100;

            // if (!colWidth && String(col.dataField).endsWith('item_name')) colWidth = 210;
            // if (!colWidth && String(col.dataField).endsWith('variant_dimens')) colWidth = 160;
            // if (!colWidth && String(col.dataField).endsWith('store_name')) colWidth = 130;
            // // if (!colWidth && String(col.dataField).endsWith('module')) colWidth = 120;
            // // if (!colWidth && String(col.dataField).endsWith('type')) colWidth = 120;

            // if (!colWidth && String(col.dataField).endsWith('entity_name')) colWidth = 200;
            // if (!colWidth && String(col.dataField).endsWith('warehouse_name')) colWidth = 170;
            // if (!colWidth && String(col.dataField).endsWith('department')) colWidth = 160;
            // if (!colWidth && String(col.dataField).endsWith('business_unit')) colWidth = 160;
            // if (!colWidth && String(col.dataField).endsWith('uom')) colWidth = 55;
            // if (!colWidth && String(col.dataField).endsWith('phone')) colWidth = 100;
            // if (!colWidth && String(col.dataField).endsWith('qty')) colWidth = col.isMarkSelected ? 60 : colWidth ? colWidth : 40;

            // if (!colWidth && colType === 'percent') colWidth = 60;

            // if (!colWidth && String(col.dataField).endsWith('name')) colWidth = 200;

            // if (!colWidth && (colType === 'bool' || colType === 'int') && String(col.dataField).startsWith('is_')) colWidth = 80;

            // if (!colWidth && colType === 'currency') {
            //     if (String(col.dataField).endsWith('unit_price')) colWidth = 90;
            //     else colWidth = 120;
            // }

            colLabel = AntLocalization.translateText(AntCondition.getValue(props.parentData, colLabel));
            col.caption = String(colLabel).trim();

            var labelWidth = AntString.measureColumnWidth(colLabel);
            // if (colLabel) labelWidth += 4;
            // if (colType === 'currency') labelWidth += 6;

            if (colLookupValues instanceof Array) {
                var displayField = col.displayField
                    ? col.displayField
                    : found && found.lookup && found.lookup.displayField
                    ? found.lookup.displayField
                    : 'name';

                for (let i = 0; i < colLookupValues.length; i++) {
                    const displayText = colLookupValues[i][displayField];
                    if (displayText) {
                        var displayWidth = AntString.measureCellWidth(displayText);

                        if (labelWidth < displayWidth) labelWidth = displayWidth;
                    }
                }
            }

            if (!colMinWidth || colMinWidth < labelWidth) colMinWidth = labelWidth;

            // if (!colMinWidth) colMinWidth = colWidth;

            col.width = colWidth;
            col.minWidth = colMinWidth;
            if (String(colLabel).trim().length <= 0) col.minWidth = undefined;
            col.allowHiding = colAllowHiding;
            if (col.allowHiding === undefined) col.allowHiding = true;

            if (!colAlignment) {
                if (String(col.dataField).endsWith('status')) colAlignment = 'center';
                if (String(col.dataField).endsWith('_code')) colAlignment = 'center';
                if (String(col.dataField).endsWith('code')) colAlignment = 'center';
                if (String(col.dataField).endsWith('_qty')) colAlignment = 'center';
                if (String(col.dataField).endsWith('qty')) colAlignment = 'center';
                if (String(col.dataField).endsWith('_pct')) colAlignment = 'center';
                if (String(col.dataField).endsWith('pct')) colAlignment = 'center';
                if (String(col.dataField).endsWith('_amt')) colAlignment = 'right';
                if (String(col.dataField).endsWith('amt')) colAlignment = 'right';
                if (String(col.dataField).endsWith('_price')) colAlignment = 'right';
                if (String(col.dataField).endsWith('price')) colAlignment = 'right';
            }

            // if (!colAlignment) colAlignment = 'left';
            col.alignment = colAlignment;

            if (colLookupValues instanceof Array) {
                col.calculateCellValue = (data) => {
                    if (data) {
                        var colValue = data[col.dataField];
                        if (colValue != null) {
                            var obj = colLookupValues.find((f) => f.code === colValue);
                            if (obj) return obj.name;
                        }
                        return colValue;
                    }
                    return null;
                };
            }

            // if (!that.state.sortByColumn && col.defaultSort) {
            //     col.sortIndex = 0;
            //     col.sortOrder = col.defaultSort.order ? col.defaultSort.order : 'asc';
            //     // that.state.sortByColumn = col.dataField;
            //     // that.state.sortType = col.sortOrder;
            // }

            if (colLookupValues instanceof Array)
                colLookupValues = colLookupValues.map((c) => {
                    c.name = AntLocalization.translateText(AntCondition.getValue(formData, c.name));
                    return c;
                });

            if (colType === 'bool' || colEditor == 'CheckBox') {
                if (!col.alignment) col.alignment = 'center';

                col.calculateCellValue = (data) => {
                    if (data) {
                        if (typeof data[col.dataField] == 'boolean') return data[col.dataField];

                        if (typeof data[col.dataField] == 'number') return data[col.dataField] != 0;

                        // if (data[col.dataField] === 1) return true;

                        if (colType === 'bool') return parseInt(data[col.dataField]) === 1;
                        if (colType === 'int') return parseInt(data[col.dataField]) > 0;
                        if (colType === 'float') return parseFloat(data[col.dataField]) > 0;
                        else return parseFloat(data[col.dataField]) > 0;
                    }
                    return false;
                };
                col.cellTemplate = 'renderCheckboxCell';
                col.dataType = 'boolean';
            }

            if (colType === 'int') {
                if (!col.format) col.format = { type: 'fixedPoint' };
                if (!col.alignment) col.alignment = 'right';
                col.calculateCellValue = (data) => {
                    if (data && data[col.dataField] != null) return parseInt(data[col.dataField]);
                    else return 0;
                };
                col.dataType = 'number';
            }

            if (colType === 'float' || colType === 'decimal') {
                if (!col.format) col.format = '#,##0.' + String('#').repeat(found && found.decimalDigit ? found.decimalDigit : 2);

                if (!col.alignment) col.alignment = 'right';
                col.calculateCellValue = (data) => {
                    if (data && data[col.dataField] != null) return parseFloat(data[col.dataField]);
                    else return 0;
                };
                col.dataType = 'number';
            }

            if (colType === 'day') {
                if (!col.format) {
                    let strEnd = AntLocalization.translateText({ vi: 'Ngày', en: 'Day', fr: 'Day', es: 'Day', id: 'Day', ja: 'Day', zh: 'Day', ko: 'Day', hi: 'Day', ar: 'Day' });
                    col.format = '#,##0 ' + strEnd;
                }
                if (!col.alignment) col.alignment = 'right';
                col.calculateCellValue = (data) => {
                    if (data && data[col.dataField] != null) return parseFloat(data[col.dataField]);
                    else return 0;
                };
                col.dataType = 'number';
            }

            if (colType === 'month') {
                if (!col.alignment) col.alignment = 'center';
                col.calculateDisplayValue = (data) => {
                    if (data && data[col.dataField] != null) {
                        var value = parseFloat(data[col.dataField]);
                        if (value) {
                            if (String(value).length > 6) {
                                return AntTime.fromUnix(value).format('MM/YYYY');
                            } else return AntTime.fromString(format('{0}-{1}-01 00:01', Math.floor(value / 100), value % 100)).format('MM/YYYY');
                        }
                    } else return '';
                };
                col.dataType = 'number';
            }

            if (col.config && col.config.dataTypeDisplay === 'month') {
                if (!col.format) {
                    let strEnd = AntLocalization.translateText({ vi: 'Tháng', en: 'Month', fr: 'Mois', es: 'Mes', id: 'Bulan', ja: '月', zh: '月', ko: '월', hi: 'महीना', ar: 'شهر' });
                    col.format = '#,##0 ' + strEnd;
                }

                if (!col.alignment) col.alignment = 'right';
                col.calculateCellValue = (data) => {
                    if (data && data[col.dataField] != null) return parseFloat(data[col.dataField]);
                    else return 0;
                };
                col.dataType = 'number';
            }

            if (colType === 'year') {
                if (!col.alignment) col.alignment = 'center';
                col.calculateDisplayValue = (data) => {
                    if (data && data[col.dataField] != null) {
                        var value = parseFloat(data[col.dataField]);
                        if (value) {
                            if (String(value).length > 6) {
                                return AntTime.fromUnix(value).format('YYYY');
                            } else return AntTime.fromString(format('{0}-01-01 00:01', parseInt(value))).format('YYYY');
                        }
                    } else return '';
                };
                col.dataType = 'number';
            }

            if (col.config && col.config.dataTypeDisplay === 'year') {
                if (!col.format) {
                    let strEnd = AntLocalization.translateText({ vi: 'Năm', en: 'Year', fr: 'Année', es: 'Año', id: 'Tahun', ja: '年', zh: '年', ko: '년', hi: 'वर्ष', ar: 'سنة' });
                    col.format = '#,##0 ' + strEnd;
                }

                if (!col.alignment) col.alignment = 'right';
                col.calculateCellValue = (data) => {
                    if (data && data[col.dataField] != null) return parseFloat(data[col.dataField]);
                    else return 0;
                };
                col.dataType = 'number';
            }
            if (colType === 'period') {
                if (!col.format) {
                    let strEnd = AntLocalization.translateText({ vi: 'kỳ', en: 'period', fr: 'période', es: 'período', id: 'periode', ja: '期間', zh: '期', ko: '기간', hi: 'अवधि', ar: 'فترة' });
                    col.format = '#,##0 ' + strEnd;
                }

                if (!col.alignment) col.alignment = 'right';
                col.calculateCellValue = (data) => {
                    if (data && data[col.dataField] != null) return parseFloat(data[col.dataField]);
                    else return 0;
                };
                col.dataType = 'number';
            }

            if (colType === 'percent') {
                if (!col.format)
                    col.format = {
                        type: 'percent',
                        precision: found.decimalDigit ? found.decimalDigit : 0
                    };
                if (!col.alignment) col.alignment = 'right';
                col.calculateCellValue = (data) => {
                    if (data && data[col.dataField] != null) return parseFloat(data[col.dataField]);
                    else return 0;
                };
                col.dataType = 'number';
            }

            if (colType === 'currency') {
                if (!col.alignment) col.alignment = 'right';

                if (col.currency) {
                    // Resolve currency per-row khi có col.currency.currencyField (giống AntGrid)
                    col.calculateCellValue = (data) => {
                        if (data && data[col.dataField] != null) return parseFloat(data[col.dataField]);
                        return null;
                    };
                    col.calculateDisplayValue = (data) => {
                        var value = data ? data[col.dataField] : null;
                        if (typeof value === 'number') value = parseFloat(value);
                        if (value === 0 && col.displayZeroAsEmpty !== false) return '';
                        if (value === null || value === undefined) return '';
                        var resolvedCurrency = AntLocalization.resolveCurrency(col.currency, data, props.parentData, props.currencyField);
                        var showSign = value > 0 && (col.showSign || (col.config && col.config.showSign && col.showSign !== false));
                        return AntLocalization.translateCurrency(value, showSign, { currency: resolvedCurrency });
                    };
                    col.dataType = 'number';
                } else {
                    if (!col.format) {
                        col.format = AntLocalization.getCurrencyFormat();
                    }
                    col.calculateCellValue = (data) => {
                        if (data && data[col.dataField] != null) return parseInt(data[col.dataField]);
                        else return 0;
                    };
                    col.dataType = 'number';
                }
            }

            if (colType === 'date') {
                if (!col.alignment) col.alignment = 'center';
                if (!col.format)
                    col.format = function (value) {
                        if (!value) return '';

                        if (AntTime.isUnixWithinToDay(value)) {
                            if (col.showTimeWithinToday) return AntTime.fromUnix(value).format('HH:mm');
                            else
                                return AntLocalization.translateText({ vi: 'Hôm nay', en: 'Today', fr: 'Aujourd\'hui', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم' });
                        }

                        if (AntTime.isUnixWithinYesterday(value))
                            return AntLocalization.translateText({ vi: 'Hôm qua', en: 'Yesterday', fr: 'Hier', es: 'Ayer', id: 'Kemarin', ja: '昨日', zh: '昨天', ko: '어제', hi: 'कल (बीता)', ar: 'أمس' });

                        if (AntTime.isUnixWithinTomorrow(value))
                            return AntLocalization.translateText({ vi: 'Ngày mai', en: 'Tomorrow', fr: 'Demain', es: 'Mañana', id: 'Besok', ja: '明日', zh: '明天', ko: '내일', hi: 'कल (आने वाला)', ar: 'غداً' });

                        if (AntTime.isUnixWithinCurrentYear(value)) return AntTime.fromUnix(value).format('DD/MM');

                        if (col.showTimeWithinToday) return AntTime.fromUnix(value).format('DD/MM');

                        return AntTime.fromUnix(value).format('DD/MM/YY');
                    };
                col.calculateCellValue = (data) => {
                    if (col.customCellValue) return col.customCellValue(data);

                    var value = data ? data[col.dataField] : null;
                    if (value === null || value === undefined) return value;
                    if (typeof value === 'number') return parseFloat(value);

                    return value;
                };
                col.calculateDisplayValue = (data) => {
                    var value = data ? data[col.dataField] : null;
                    if (typeof value === 'number') value = parseFloat(value);

                    if (col.customDisplay) value = col.customDisplay(data);
                    else if (col.config && col.config.customDisplay) value = col.config.customDisplay(data);

                    if (value === 0 && col.displayZeroAsEmpty !== false) value = '';
                    if (value === null || value === undefined) value = '';

                    return value;
                };
                col.dataType = 'date';
            }

            if (colType === 'datetime') {
                if (!col.alignment) col.alignment = 'center';
                if (!col.format)
                    col.format = function (value) {
                        if (!value) return '';

                        if (AntTime.isUnixWithinToDay(value)) return AntTime.fromUnix(value).format('HH:mm');

                        if (AntTime.isUnixWithinCurrentYear(value)) return AntTime.fromUnix(value).format('DD/MM HH:mm');

                        return AntTime.fromUnix(value).format('DD/MM/YY');
                    };

                col.calculateCellValue = (data) => {
                    if (col.customCellValue) return col.customCellValue(data);

                    var value = data ? data[col.dataField] : null;
                    if (value === null || value === undefined) return value;
                    if (typeof value === 'number') return parseFloat(value);

                    return value;
                };

                col.calculateDisplayValue = (data) => {
                    var value = data ? data[col.dataField] : null;
                    if (typeof value === 'number') value = parseFloat(value);

                    if (col.customDisplay) value = col.customDisplay(data);
                    else if (col.config && col.config.customDisplay) value = col.config.customDisplay(data);

                    if (value === 0 && col.displayZeroAsEmpty !== false) value = '';
                    if (value === null || value === undefined) value = '';

                    return value;
                };

                col.dataType = 'date';
            }

            if (colType === 'time') {
                if (!col.alignment) col.alignment = 'center';
                if (!col.format) col.format = 'hh:mm a';

                col.calculateCellValue = (data) => {
                    if (col.customCellValue) return col.customCellValue(data);

                    var value = data ? data[col.dataField] : null;
                    if (value === null || value === undefined) return value;
                    if (typeof value === 'number') return parseFloat(value);

                    return value;
                };

                col.calculateDisplayValue = (data) => {
                    var value = data ? data[col.dataField] : null;
                    if (typeof value === 'number') value = AntTime.fromUnix(parseFloat(value)).toDate();

                    if (col.customDisplay) value = col.customDisplay(data);
                    else if (col.config && col.config.customDisplay) value = col.config.customDisplay(data);

                    if (value === 0 && col.displayZeroAsEmpty !== false) value = '';
                    if (value === null || value === undefined) value = '';

                    return value;
                };

                col.dataType = 'date';
            }

            if (colType === 'join') {
                col.calculateCellValue = (data) => {
                    if (col.customCellValue) return col.customCellValue(data);
                    var fields = col.fields;
                    if (fields instanceof Array) {
                        var tableName = col.cnfTable
                            ? col.cnfTable
                            : props.gridOptions && props.gridOptions.table
                            ? props.gridOptions.table
                            : props.table
                            ? props.table
                            : props.API.entityName;

                        var ar = [];

                        for (let i = 0; i < fields.length; i++) {
                            const fField = fields[i];

                            var found = config.getCnfField(fField, tableName);
                            if (found && found.cnfTable) found = config.getCnfField(found.cnfField, found.cnfTable ? found.cnfTable : tableName);

                            var valueField = found && found.lookup && found.lookup.valueField ? found.lookup.valueField : 'code';
                            var displayField = found && found.lookup && found.lookup.displayField ? found.lookup.displayField : 'name';
                            var lookupValues = found && found.lookup && found.lookup.values instanceof Array ? found.lookup.values : [];

                            var fValue = data[fField];
                            if (fValue != null) {
                                var obj = lookupValues.find((f) => f[valueField] === fValue);
                                if (obj) fValue = obj[displayField];
                            }

                            if (typeof fValue === 'string') fValue = String(fValue);
                            if (fValue === AntLocalization.translateText({ vi: 'Không', en: 'None', fr: 'Aucun', es: 'Ninguno', id: 'Tidak ada', ja: 'なし', zh: '无', ko: '없음', hi: 'कोई नहीं', ar: 'لا شيء' }) && col.displayNoneAsEmpty !== false)
                                fValue = '';
                            if (fValue === null || fValue === undefined) fValue = '';

                            if (fValue && parseInt(fValue) && String(fField).endsWith('_adm2_name'))
                                fValue = AntLocalization.translateText({ vi: 'Quận', en: 'Dist', fr: 'District', es: 'Distrito', id: 'Distrik', ja: '地区', zh: '区', ko: '구역', hi: 'जिला', ar: 'منطقة' }) + ' ' + fValue;

                            ar.push(fValue);
                        }

                        ar = AntArray.distinct(
                            ar
                                .flat()
                                .filter((f) => Boolean(f))
                                .map((f) => String(f))
                        );
                        return ar.join(col.separator ? col.separator : ', ');
                    }

                    return '';
                };
                col.dataType = 'string';
            }
            if (colType === 'first') {
                col.calculateCellValue = (data) => {
                    if (col.customCellValue) return col.customCellValue(data);
                    var fields = col.fields;
                    if (fields instanceof Array) {
                        var tableName = col.cnfTable
                            ? col.cnfTable
                            : props.gridOptions && props.gridOptions.table
                            ? props.gridOptions.table
                            : props.table
                            ? props.table
                            : props.API.entityName;

                        var ar = [];

                        for (let i = 0; i < fields.length; i++) {
                            const fField = fields[i];

                            var found = config.getCnfField(fField, tableName);
                            if (found && found.cnfTable) found = config.getCnfField(found.cnfField, found.cnfTable ? found.cnfTable : tableName);

                            var valueField = found && found.lookup && found.lookup.valueField ? found.lookup.valueField : 'code';
                            var displayField = found && found.lookup && found.lookup.displayField ? found.lookup.displayField : 'name';
                            var lookupValues = found && found.lookup && found.lookup.values instanceof Array ? found.lookup.values : [];

                            var fValue = data[fField];
                            if (fValue != null) {
                                var obj = lookupValues.find((f) => f[valueField] === fValue);
                                if (obj) fValue = obj[displayField];
                            }

                            if (typeof fValue === 'string') fValue = String(fValue);
                            if (fValue === AntLocalization.translateText({ vi: 'Không', en: 'None', fr: 'Aucun', es: 'Ninguno', id: 'Tidak ada', ja: 'なし', zh: '无', ko: '없음', hi: 'कोई नहीं', ar: 'لا شيء' }) && col.displayNoneAsEmpty !== false)
                                fValue = '';
                            if (fValue === null || fValue === undefined) fValue = '';
                            ar.push(fValue);
                        }
                        return ar
                            .filter((f) => Boolean(f))
                            .map((f) => String(f))
                            .shift();
                    }

                    return '';
                };
                col.dataType = 'string';
            }
            if (colType === 'last') {
                col.calculateCellValue = (data) => {
                    if (col.customCellValue) return col.customCellValue(data);
                    var fields = col.fields;
                    if (fields instanceof Array) {
                        var tableName = col.cnfTable
                            ? col.cnfTable
                            : props.gridOptions && props.gridOptions.table
                            ? props.gridOptions.table
                            : props.table
                            ? props.table
                            : props.API.entityName;

                        var ar = [];

                        for (let i = 0; i < fields.length; i++) {
                            const fField = fields[i];

                            var found = config.getCnfField(fField, tableName);
                            if (found && found.cnfTable) found = config.getCnfField(found.cnfField, found.cnfTable ? found.cnfTable : tableName);

                            var valueField = found && found.lookup && found.lookup.valueField ? found.lookup.valueField : 'code';
                            var displayField = found && found.lookup && found.lookup.displayField ? found.lookup.displayField : 'name';
                            var lookupValues = found && found.lookup && found.lookup.values instanceof Array ? found.lookup.values : [];

                            var fValue = data[fField];
                            if (fValue != null) {
                                var obj = lookupValues.find((f) => f[valueField] === fValue);
                                if (obj) fValue = obj[displayField];
                            }

                            if (typeof fValue === 'string') fValue = String(fValue);
                            if (fValue === AntLocalization.translateText({ vi: 'Không', en: 'None', fr: 'Aucun', es: 'Ninguno', id: 'Tidak ada', ja: 'なし', zh: '无', ko: '없음', hi: 'कोई नहीं', ar: 'لا شيء' }) && col.displayNoneAsEmpty !== false)
                                fValue = '';
                            if (fValue === null || fValue === undefined) fValue = '';
                            ar.push(fValue);
                        }
                        return ar
                            .filter((f) => Boolean(f))
                            .map((f) => String(f))
                            .pop();
                    }

                    return '';
                };
                col.dataType = 'string';
            }

            if (!col.alignment && String(col.dataField).endsWith('status')) col.alignment = 'center';
            if (!col.alignment) col.alignment = 'left';
            if (col.link) col.cellTemplate = 'renderLinkCell';

            var visible = true;
            if (typeof col.visible === 'object') {
                if (col.visible.type === 'action' && col.visible.field) {
                    visible = false;
                    var cond = actionConditions.find((cond) => cond.name === col.visible.field);
                    if (cond && cond.value !== undefined) visible = cond.value === col.visible.value;
                } else {
                    visible = AntCondition.isVisible(formData, col);
                }
            } else if (typeof col.visible === 'boolean') visible = col.visible;

            if (visible) {
                if (col.config) visible = AntCondition.isVisible(formData, col.config);
            }
            col.visible = visible;
        });

        var minHidingPriority = Number.MAX_SAFE_INTEGER;
        columns.forEach((col) => {
            if (!col.isSystemCol && col.hidingPriority !== undefined && col.hidingPriority < minHidingPriority)
                minHidingPriority = col.hidingPriority;
        });

        var ar = [];
        columns.forEach((col) => {
            if (!col.isSystemCol && col.hidingPriority === undefined) ar.push(col);
        });
        for (let i = 0; i < ar.length; i++) {
            var col1 = ar[i];
            var col2 = ar[ar.length - i - 1];
            if (col1.hidingPriority === undefined) {
                minHidingPriority--;
                col1.hidingPriority = minHidingPriority;
            }
            if (col2.hidingPriority === undefined) {
                minHidingPriority--;
                col2.hidingPriority = minHidingPriority;
            }
        }

        minHidingPriority = Number.MAX_SAFE_INTEGER;
        columns.forEach((col) => {
            if (!col.isSystemCol && col.hidingPriority !== undefined && col.hidingPriority < minHidingPriority)
                minHidingPriority = col.hidingPriority;
        });

        columns.forEach((col) => {
            if (!col.isSystemCol && col.hidingPriority !== undefined) col.hidingPriority = col.hidingPriority - minHidingPriority;
        });

        if (rawColumns instanceof Array) {
            rawColumns.forEach((rawCol) => {
                var col = columns.find((c) => c.name === 'col' + rawCol.dataField);
                if (col) {
                    var visible = rawCol.visible !== undefined ? Boolean(rawCol.visible) : true;
                    if (typeof rawCol.visible === 'boolean') visible = rawCol.visible;
                    else if (typeof rawCol.visible === 'object') {
                        if (rawCol.visible.type === 'action' && rawCol.visible.field) {
                            visible = false;
                            var condKey = Object.keys(props.searchConditions).find((k) => k === rawCol.visible.field);
                            if (condKey && props.searchConditions[condKey] !== undefined)
                                visible = props.searchConditions[condKey] === rawCol.visible.value;
                        } else if (props.parentData) {
                            visible = AntCondition.isVisible(props.parentData, rawCol);
                        }
                    }

                    if (visible && col.config && typeof col.config.visible === 'object') {
                        visible = AntCondition.isVisible(props.parentData, col.config);
                    }

                    if (rawCol.hideEmpty !== false && dataSource) {
                        var isEmptyCol = dataSource.isEmptyColumn(rawCol.dataField);
                        visible = visible && !isEmptyCol;
                    }
                    col.visible = visible;
                }
            });

            var count = columns.filter((col) => col.visible && col.width === undefined).length;
            if (count <= 0) {
                rawColumns.forEach((rawCol) => {
                    var col = columns.find((c) => c.name === 'col' + rawCol.dataField);
                    if (col && col.width === undefined) {
                        var visible = rawCol.visible !== undefined ? Boolean(rawCol.visible) : true;

                        if (typeof rawCol.visible === 'boolean') visible = rawCol.visible;
                        else if (typeof rawCol.visible === 'object') {
                            if (rawCol.visible.type === 'action' && rawCol.visible.field) {
                                visible = false;
                                var condKey = Object.keys(props.searchConditions).find((k) => k === rawCol.visible.field);
                                if (condKey && props.searchConditions[condKey] !== undefined)
                                    visible = props.searchConditions[condKey] === rawCol.visible.value;
                            } else if (props.parentData) {
                                visible = AntCondition.isVisible(props.parentData, rawCol);
                            }
                        }

                        if (visible && col.config && typeof col.config.visible === 'object') {
                            visible = AntCondition.isVisible(props.parentData, col.config);
                        }

                        col.visible = visible;
                    }
                });
            }
        }
        var isOK = columns.some((c) => c.visible && c.width === 'auto');
        if (!isOK) {
            var col = columns.find((c) => c.visible && Boolean(String(c.dataField).endsWith('description') || String(c.dataField).endsWith('_desc')));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('variant_dimens'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('item_name'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('entity_name'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('store_name'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('warehouse_name'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('job_title'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('business_unit'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('department'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('status'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('name'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('code'));
            if (!col) col = columns.find((c) => c.visible && String(c.dataField).endsWith('type'));

            if (col) {
                col.width = 'auto';
            } else {
                col = columns.find(
                    (c) => !c.visible && Boolean(String(c.dataField).endsWith('description') || String(c.dataField).endsWith('_desc'))
                );
                if (col) {
                    col.width = 'auto';
                    col.visible = true;
                }
            }
        }

        isOK = columns.some((c) => c.visible && c.width === 'auto');
        if (!isOK) {
            var maxCol = null;
            var maxWidth = 0;

            columns
                .filter((c) => c.visible && c.width !== 'auto')
                .forEach((c) => {
                    if (typeof c.width === 'number' && parseFloat(c.width) > maxWidth) {
                        maxWidth = parseFloat(c.width);
                        maxCol = c;
                    } else if (typeof c.minWidth === 'number' && parseFloat(c.minWidth) > maxWidth) {
                        maxWidth = parseFloat(c.minWidth);
                        maxCol = c;
                    }
                });
            if (maxCol) maxCol.width = 'auto';
        }

        columns = columns.map((c) => {
            if (c.width === 'auto') c.width = undefined;
            else if (!c.width) c.width = 'auto';
            return c;
        });

        return columns;
    }, [
        rawColumns,
        dataSource,
        props.gridOptions,
        props.searchConditions,
        props.parentData,
        props.table,
        props.API,
        props.formRef,
        props.formRef?.current
    ]);

    const mainColumns = useMemo(() => {
        const columns = initColumns();
        // Loại editor của dòng lọc (chỉ cột có trên bảng — BE mới lọc được).
        const cnfOf = (name) => {
            if (!config || !config.getCnfField) return null;
            let found = config.getCnfField(name, tableName);
            if (found && found.cnfTable) found = config.getCnfField(found.cnfField || name, found.cnfTable) || found;
            return found || null;
        };
        columns.forEach((col) => {
            // Độ rộng ban đầu (cấu hình / tự đo) — "tự căn" (nhấp đúp ranh giới cột) trả về đây.
            if (col && col._antInitialWidth === undefined) col._antInitialWidth = col.width === undefined ? null : col.width;
            const spec = col && col.dataField ? resolveColumnFilterSpec(col, col, cnfOf, (v) => AntLocalization.translateText(AntCondition.getValue(null, v)), (item) => AntCondition.isVisible(null, item)) : null;
            if (spec) col._antFilter = spec;
            else delete col._antFilter;
            col.allowFiltering = Boolean(spec);
        });
        return columns;
    }, [initColumns]);
    const mainColumnsRef = useRef(mainColumns);
    mainColumnsRef.current = mainColumns;

    // Áp cấu hình đã lưu thẳng vào lưới (không qua props — devextreme-react sẽ trả option lệch props về cũ).
    const applySavedLayout = useCallback((instance) => {
        if (!instance) return;
        const defs = mainColumnsRef.current || [];
        const widths = getSavedColumnWidths(savedSettings.current);
        const byDefault = defs.slice();
        const ordered = userOrder.current ? applyColumnOrder(byDefault, userOrder.current, (c) => Boolean(c && c.dataField)) : byDefault;
        applyingLayout.current = true;
        instance.beginUpdate();
        try {
            ordered.forEach((col, index) => {
                const key = col.name || col.dataField;
                if (userOrder.current) instance.columnOption(key, 'visibleIndex', index);
                if (col.dataField && widths[col.dataField]) instance.columnOption(key, 'width', widths[col.dataField]);
                if (col.dataField && userHidden.current[col.dataField] === false && col.allowHiding !== false) instance.columnOption(key, 'visible', false);
                const sortOrder = userSort.current && userSort.current.field === col.dataField ? userSort.current.order || 'asc' : undefined;
                if (userSort.current) instance.columnOption(key, 'sortOrder', sortOrder);
            });
            instance.option('filterRow', { visible: filterRowVisible.current, showOperationChooser: false, applyFilter: 'auto' });
        } finally {
            instance.endUpdate();
            applyingLayout.current = false;
        }
    }, []);

    // Nhấp đúp ranh giới 2 cột ở tiêu đề: trả mọi cột về độ rộng ban đầu (DevExtreme tự đo theo nội dung) và bỏ độ
    // rộng người dùng đã kéo (cả đã lưu).
    const autoFitColumns = useCallback(
        (instance) => {
            if (!instance) return;
            const defs = mainColumnsRef.current || [];
            const patch = {};
            applyingLayout.current = true;
            instance.beginUpdate();
            try {
                defs.forEach((col) => {
                    if (!col.dataField) return;
                    const width = col._antInitialWidth === null ? undefined : col._antInitialWidth;
                    col.width = width;
                    instance.columnOption(col.name || col.dataField, 'width', width);
                    patch[col.dataField] = { width: null };
                });
            } finally {
                instance.endUpdate();
                applyingLayout.current = false;
            }
            savedSettings.current = { ...savedSettings.current, columns: mergeColumnSettings(savedSettings.current.columns, patch) };
            saveSettings((entry) => ({ ...entry, columns: mergeColumnSettings(entry.columns, patch) }));
            instance.updateDimensions();
        },
        [saveSettings]
    );

    const attachAutoFit = useCallback(
        (instance) => {
            const root = instance && instance.element ? instance.element() : null;
            const element = root && root.get ? root.get(0) : root;
            if (!element) return;
            const detach = attachHeaderBoundaryDblClick(element, {
                hint: AntLocalization.translateText({
                    vi: 'Kéo để đổi độ rộng · Nhấp đúp để tự căn độ rộng các cột',
                    en: 'Drag to resize · Double-click to auto-fit all columns',
                    fr: 'Glisser pour redimensionner · Double-clic : ajuster les colonnes',
                    es: 'Arrastre para ajustar · Doble clic: autoajustar columnas',
                    id: 'Seret untuk ubah lebar · Klik ganda: sesuaikan semua kolom',
                    ja: 'ドラッグで幅変更 · ダブルクリックで全列自動調整',
                    zh: '拖动调整宽度 · 双击自动适应所有列',
                    ko: '드래그로 너비 조정 · 더블클릭으로 전체 열 자동 맞춤',
                    hi: 'आकार बदलने हेतु खींचें · डबल-क्लिक: कॉलम ऑटो-फ़िट',
                    ar: 'اسحب لتغيير العرض · نقر مزدوج للضبط التلقائي للأعمدة'
                }),
                onAutoFit: () => autoFitColumns(instance)
            });
            if (instance.on) instance.on('disposing', detach);
        },
        [autoFitColumns]
    );

    // Người dùng kéo độ rộng / bấm sắp xếp → lưu. Thay đổi do mã (applyingLayout) bỏ qua.
    const onGridOptionChanged = useCallback(
        (e) => {
            if (applyingLayout.current || e.name !== 'columns') return;
            const m = /^columns\[(\d+)\]\.(width|sortOrder)$/.exec(String(e.fullName));
            if (!m) return;
            const column = e.component.columnOption(parseInt(m[1], 10));
            const field = column && column.dataField;
            if (!field) return;
            if (m[2] === 'width' && typeof e.value === 'number' && e.value > 20) {
                const def = (mainColumnsRef.current || []).find((c) => c.dataField === field);
                if (def) def.width = e.value;
                saveSettings((entry) => ({ ...entry, columns: mergeColumnSettings(entry.columns, { [field]: { width: Math.round(e.value) } }) }));
            }
            if (m[2] === 'sortOrder') {
                userSort.current = e.value ? { field, order: e.value } : null;
                saveSettings((entry) => ({ ...entry, sort: userSort.current ? { ...userSort.current } : null }));
            }
        },
        [saveSettings]
    );

    const renderCheckboxCell = useCallback((cell) => {
        return <CheckBox value={cell.value} hoverStateEnabled={false} activeStateEnabled={false} focusStateEnabled={false} />;
    }, []);

    const rowClick = useCallback((e) => {
        if (grid.current && grid.current.instance) {
            grid.current.instance.selectRows([e.key]);
        }
    }, []);

    const searchTimeout = useRef();
    const onSearchText = useCallback((text) => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            const next = text === undefined || text === null ? '' : String(text);
            if (next === searchTextRef.current) return;
            searchTextRef.current = next;
            if (grid.current && grid.current.instance) grid.current.instance.refresh();
        }, 400);
    }, []);

    const onRowPrepared = useCallback(
        (e2) => {
            let listener = () => rowClick(e2);
            e2.rowElement.removeEventListener('click', listener);
            e2.rowElement.addEventListener('click', listener);
        },
        [rowClick]
    );

    const onSelectionChangedCat1 = useCallback((e) => {
        selectedCats1.current = e.selectedItems;
        if (grid.current && grid.current.instance) grid.current.instance.refresh();
    }, []);

    const onGridSelectionChanged = useCallback(
        (e2, e) => {
            // var newValue = e2.selectedRowKeys[0];
            var rowData = e2.selectedRowsData instanceof Array && e2.selectedRowsData.length > 0 ? e2.selectedRowsData[0] : null;
            var newValue = rowData ? rowData[props.gridOptions.valueExpr] : undefined;
            var newDisplay = rowData ? rowData[props.gridOptions.displayExpr] : undefined;

            if (newValue !== refMainValue.current) {
                if (newValue !== undefined) {
                    var previousValue = refMainValue.current;
                    refMainValue.current = newValue;
                    refByPassOnValueChangedEvent.current = true;
                    setTimeout(() => {
                        refByPassOnValueChangedEvent.current = false;
                    }, 1000);
                    setMainValue(newValue);

                    onDataChanged({
                        value: newValue,
                        display: newDisplay,
                        previousValue: previousValue,
                        rowData: rowData
                    });

                    e.component.close();
                }
            }
        },
        [props.gridOptions, onDataChanged]
    );

    // ---- Ẩn hiện / thứ tự cột (bảng chọn cột trên thanh công cụ popup) ----
    const getColumnChooserItems = useCallback(() => {
        const instance = grid.current && grid.current.instance;
        const defs = (mainColumnsRef.current || []).filter((c) => c.dataField && c.caption);
        const positionOf = (col) => {
            const vi = instance ? instance.columnOption(col.name || col.dataField, 'visibleIndex') : undefined;
            return typeof vi === 'number' ? vi : defs.indexOf(col);
        };
        return defs
            .slice()
            .sort((a, b) => positionOf(a) - positionOf(b))
            .map((col) => {
                const hidden = userHidden.current[col.dataField] === false;
                return {
                    field: col.dataField,
                    caption: String(col.caption),
                    visible: !hidden,
                    locked: col.allowHiding === false && !hidden,
                    customized: hidden || Boolean(userOrder.current)
                };
            });
    }, []);

    const applyColumnOrderToGrid = useCallback(() => {
        const instance = grid.current && grid.current.instance;
        if (!instance) return;
        const defs = mainColumnsRef.current || [];
        const ordered = userOrder.current ? applyColumnOrder(defs.slice(), userOrder.current, (c) => Boolean(c && c.dataField)) : defs;
        applyingLayout.current = true;
        instance.beginUpdate();
        try {
            ordered.forEach((col, index) => instance.columnOption(col.name || col.dataField, 'visibleIndex', index));
        } finally {
            instance.endUpdate();
            applyingLayout.current = false;
        }
    }, []);

    const onColumnVisibilityChange = useCallback(
        (field, visible) => {
            const next = { ...userHidden.current };
            if (visible) delete next[field];
            else next[field] = false;
            userHidden.current = next;
            saveSettings((entry) => ({ ...entry, columns: mergeColumnSettings(entry.columns, { [field]: { visible: visible ? null : false } }) }));
            resetColumnVisibilityRef.current();
        },
        [saveSettings]
    );

    const onColumnVisibilityReset = useCallback(() => {
        const fields = Object.keys(userHidden.current);
        const patch = {};
        fields.forEach((f) => {
            patch[f] = { visible: null };
        });
        userHidden.current = {};
        userOrder.current = null;
        saveSettings((entry) => ({ ...entry, columns: mergeColumnSettings(entry.columns, patch), columnOrder: null }));
        resetColumnVisibilityRef.current();
        applyColumnOrderToGrid();
    }, [saveSettings, applyColumnOrderToGrid]);

    const onColumnOrderChange = useCallback(
        (fields) => {
            if (!(fields instanceof Array) || !fields.length) return;
            userOrder.current = fields.slice();
            saveSettings((entry) => ({ ...entry, columnOrder: fields.slice() }));
            applyColumnOrderToGrid();
        },
        [saveSettings, applyColumnOrderToGrid]
    );

    // ---- Dòng lọc trên cột ----
    const setColumnFilterValue = useCallback((field, value, immediate) => {
        const next = { ...columnFilterValues.current };
        if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) delete next[field];
        else next[field] = value;
        columnFilterValues.current = next;
        clearTimeout(columnFilterTimer.current);
        const apply = () => {
            if (grid.current && grid.current.instance) grid.current.instance.refresh();
        };
        if (immediate) apply();
        else columnFilterTimer.current = setTimeout(apply, 500);
    }, []);

    const onGridEditorPreparing = useCallback(
        (e) => {
            if (e.parentType !== 'filterRow') return;
            const def = (mainColumnsRef.current || []).find((c) => c.dataField === e.dataField);
            prepareColumnFilterEditor(e, {
                spec: def && def._antFilter,
                value: columnFilterValues.current[e.dataField],
                onChange: (value, immediate) => setColumnFilterValue(e.dataField, value, immediate),
                focusState: activeFilterEditor
            });
        },
        [setColumnFilterValue]
    );

    const onToggleColumnFilter = useCallback(() => {
        const next = !filterRowVisible.current;
        filterRowVisible.current = next;
        const instance = grid.current && grid.current.instance;
        if (instance) instance.option('filterRow.visible', next);
        const hadValues = hasColumnFilterValues(columnFilterValues.current);
        if (!next) columnFilterValues.current = {};
        saveSettings((entry) => {
            const view = { ...(entry.view || {}) };
            if (next) view.showFilterRow = true;
            else delete view.showFilterRow;
            return { ...entry, view: Object.keys(view).length ? view : null };
        });
        if (!next && hadValues && instance) instance.refresh();
        return next;
    }, [saveSettings]);

    const hasColumnTools = (mainColumns || []).filter((c) => c.dataField).length > 1;

    const renderContent = useCallback(
        (e) => {
            var showSearchBar = false;

            var searchBarWidth = props.width;
            if (props.gridOptions.searchBar === true) {
                searchBarWidth = 200;
                showSearchBar = true;
            } else if (typeof props.gridOptions.searchBar === 'object' && props.gridOptions.searchBar !== null && props.gridOptions.searchBar.width) {
                searchBarWidth = props.gridOptions.searchBar.width;
                showSearchBar = true;
            }
            showSearchBar = showSearchBar && !props.acceptCustomValue;

            return (
                <React.Fragment>
                    {(showSearchBar || hasColumnTools || (props.gridOptions.actionItems instanceof Array && props.gridOptions.actionItems.length > 0)) && (
                        <AntGridBar
                            ref={actionBar}
                            showSearchBar={showSearchBar}
                            // Ẩn hiện cột + lọc trên cột (cấu hình lưu theo editor — AntGridSettings).
                            showFieldChooser={hasColumnTools}
                            getColumnChooserItems={hasColumnTools ? getColumnChooserItems : undefined}
                            onColumnVisibilityChange={onColumnVisibilityChange}
                            onColumnVisibilityReset={onColumnVisibilityReset}
                            onColumnOrderChange={onColumnOrderChange}
                            showColumnFilter={hasColumnTools}
                            columnFilterActive={filterRowVisible.current}
                            onToggleColumnFilter={onToggleColumnFilter}
                            showRefresh={false}
                            locationSearchBar={'before'}
                            focusStateEnabled={false}
                            searchBarWidth={searchBarWidth}
                            // searchText={props.gridOptions.searchBar ? searchText : null}
                            onSearchText={onSearchText}
                            onAction={onAction}
                            gridBarSearchClass={'ant-dropgrid-bar-search'}
                            autoFocusSearchBar={false}
                            showActions={props.gridOptions && props.gridOptions.showActions !== undefined ? props.gridOptions.showActions : undefined}
                            items={props.gridOptions.actionItems instanceof Array ? props.gridOptions.actionItems : []}
                        />
                    )}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', flexDirection: 'row-reverse' }}>
                        {props.gridOptions?.searchCategories1 && (
                            <AntGridCategories
                                API={props.API}
                                module={
                                    props.gridOptions && props.gridOptions.module
                                        ? props.gridOptions.module
                                        : props.module
                                        ? props.module
                                        : props.API.moduleName
                                }
                                table={
                                    props.gridOptions && props.gridOptions.table
                                        ? props.gridOptions.table
                                        : props.table
                                        ? props.table
                                        : props.API.entityName
                                }
                                withoutBorder={true}
                                gridSettingsKey={settingsKey}
                                gridSettingsMeta={settingsMeta}
                                searchText={searchTextRef.current}
                                searchConditions={props.searchConditions}
                                searchCategories={props.gridOptions?.searchCategories1}
                                selectedItems={selectedCats1.current}
                                // autoSelectFirst={
                                //     props.autoSelectFirstCategory &&
                                //     selectedCats1.current.length + selectedCats2.current.length + selectedCats3.current.length + selectedCats4.current.length + selectedCats5.current.length <= 0
                                // }
                                // onLoadedData={(e) => {
                                //     itemsCats1.current = e.allItems;
                                //     // itemsCats1Loaded.current = true;
                                // }}
                                onSelectionChanged={onSelectionChangedCat1}
                                // visible={cats1Visible}
                                // allowQuery={() => {
                                //     if (props.gridOptions?.searchCategories1.visibleDependOthers) return selectedCategories.length > 0;
                                //     return true;
                                // }}
                            ></AntGridCategories>
                        )}
                    </div>

                    <DataGrid
                        ref={grid}
                        noDataText={AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })}
                        remoteOperations={props.isRemote}
                        dataSource={props.isRemote && dataSource ? dataSource.instance() : props.gridOptions.dataSource}
                        keyExpr={props.gridOptions.valueExpr}
                        cacheEnabled={true}
                        showColumnHeaders={!props.groupByField && props.gridOptions.showHeader !== false && mainColumns && mainColumns.length > 1}
                        columns={mainColumns}
                        columnAutoWidth={true}
                        allowColumnResizing={true}
                        columnResizingMode={'nextColumn'}
                        columnHidingEnabled={true}
                        hoverStateEnabled={true}
                        focusStateEnabled={false}
                        rowAlternationEnabled={true}
                        wordWrapEnabled={false}
                        loadPanel={{ enabled: false }}
                        keyboardNavigation={
                            props.keyboardNavigation !== undefined
                                ? props.keyboardNavigation
                                : {
                                      enabled: false
                                  }
                        }
                        // headerFilter={{ visible: true, allowSearch: true }}
                        // filterPanel={{visible:true}}
                        // filterRow={{
                        //   visible:
                        //     props.gridOptions.filterRow !== undefined
                        //       ? props.gridOptions.filterRow
                        //       : false,
                        //   showOperationChooser: false,
                        // }}
                        onRowPrepared={onRowPrepared}
                        onOptionChanged={onGridOptionChanged}
                        onEditorPreparing={onGridEditorPreparing}
                        sorting={{ mode: 'single' }}
                        selectedRowKeys={[mainValue !== undefined && mainValue !== null ? mainValue : undefined]}
                        onSelectionChanged={(e2) => onGridSelectionChanged(e2, e)}
                        // sorting={{
                        //     mode: 'single',
                        //     clearText: 'Bỏ sắp xếp',
                        //     ascendingText: 'A->Z',
                        //     descendingText: 'Z->A'
                        // }}

                        // grouping={{
                        //     allowCollapsing: true,
                        //     autoExpandAll: true,
                        //     expandMode: 'rowClick',
                        //     texts: {
                        //         groupContinuedMessage: '',
                        //         groupContinuesMessage: ''
                        //     }
                        // }}
                        onInitialized={(e2) => {
                            e2.component.columnOption('command:adaptive', 'visible', false);
                            applySavedLayout(e2.component);
                            attachAutoFit(e2.component);
                        }}
                        height={props.height ? props.height : '300'}
                        width={'100%'}
                        // onContentReady={(e2) => {
                        //     var cols = e2.component.getVisibleColumns();
                        //     cols = cols.filter((c) => c.visibleWidth !== 'adaptiveHidden');

                        //     // console.log('ant-log-cols=', cols);
                        //     if (cols.length === 1) e2.component.option('showColumnHeaders', false);
                        //     else e2.component.option('showColumnHeaders', true);
                        // }}
                    >
                        {/* <Grouping expandMode="rowClick" /> */}
                        <Selection mode="single" />
                        <Scrolling mode="infinite" preloadEnabled={true} rowRenderingMode="virtual" />
                        <Paging enabled={true} pageSize={props.defaultPageSize ? props.defaultPageSize : 30} />
                        {/* <FilterRow visible={props.gridOptions.showFilter} /> */}
                        <Template name={'renderCheckboxCell'} render={renderCheckboxCell} />
                    </DataGrid>
                </React.Fragment>
            );
        },
        [
            dataSource,
            mainColumns,
            mainValue,
            onGridSelectionChanged,
            renderCheckboxCell,
            hasColumnTools,
            getColumnChooserItems,
            onColumnVisibilityChange,
            onColumnVisibilityReset,
            onColumnOrderChange,
            onToggleColumnFilter,
            onGridOptionChanged,
            onGridEditorPreparing,
            applySavedLayout,
            attachAutoFit,
            settingsKey,
            settingsMeta,
            props.width,
            props.gridOptions,
            props.isRemote,
            props.groupByField,
            props.keyboardNavigation,
            props.defaultPageSize,
            onRowPrepared,
            resetDataSource
        ]
    );

    const resetColumnVisibility = useCallback(() => {
        if (props.gridOptions && props.gridOptions.columns instanceof Array)
            props.gridOptions.columns.forEach((col) => {
                var visible = true;
                if (typeof col.visible === 'object' && col.visible.type === 'action' && col.visible.field) {
                    visible = false;
                    var cond = actionConditions.find((cond) => cond.name === col.visible.field);
                    if (cond && cond.value !== undefined) visible = cond.value === col.visible.value;
                } else if (typeof col.visible === 'boolean') visible = col.visible;

                if (visible) {
                    if (typeof col.visible === 'object') visible = AntCondition.isVisible(formData, col);
                    else if (col.config) {
                        var formData = null;

                        if (props.formRef && props.formRef.current && props.formRef.current.instance)
                            formData = props.formRef.current.instance.option('formData');

                        visible = AntCondition.isVisible(formData, col.config);
                    }
                }
                // if (visible) {
                //     if (col.hideEmpty !== false && dataSource) {
                //         var isEmptyCol = dataSource.isEmptyColumn(col.dataField);
                //         visible = visible && !isEmptyCol;
                //     }
                // }
                // Người dùng tắt cột trong bảng ẩn hiện cột.
                const field = typeof col === 'string' ? col : col.dataField;
                if (!field) return;
                if (visible && userHidden.current[field] === false && col.allowHiding !== false) visible = false;
                if (grid.current && grid.current.instance) {
                    applyingLayout.current = true;
                    try {
                        grid.current.instance.columnOption('col' + field, 'visible', visible);
                    } finally {
                        applyingLayout.current = false;
                    }
                }
            });
    }, [props.gridOptions, props.formRef, props.formRef?.current]);
    const resetColumnVisibilityRef = useRef(resetColumnVisibility);
    resetColumnVisibilityRef.current = resetColumnVisibility;

    const resetUI = useCallback(() => {
        resetColumnVisibility();
    }, [resetColumnVisibility]);

    const onContentReady = useCallback(
        (e) => {
            if (props.onContentReady) props.onContentReady();
            resetUI();
        },
        [props.onContentReady, resetUI]
    );

    const onAction = useCallback(
        async (e) => {
            if (e.name) {
                var cond = actionConditions.find((c) => c.name === e.name);
                if (cond) {
                    cond.value = e.value;
                } else setActionConditions([...actionConditions, { name: e.name, value: e.value }]);
            }

            if (grid.current && grid.current.instance) grid.current.instance.refresh();
        },
        [actionConditions]
    );

    const renderLookupButton = useCallback(() => {
        return (
            <i
                className={'dx-icon dx-icon-search'}
                style={{
                    marginTop: '8px',
                    display: 'inline-block',
                    fontSize: '18px'
                }}
            ></i>
        );
    }, []);

    const onValueChanged = useCallback(
        (e) => {
            if (props.acceptCustomValue || ((e.value === '' || e.value === null) && refMainValue.current !== '' && refMainValue.current !== null)) {
                // setMainValue(e.value);

                if (e.value !== refMainValue.current)
                    if (!refUpdateTimeOut.current)
                        refUpdateTimeOut.current = setTimeout(() => {
                            if (refUpdateTimeOut.current) clearTimeout(refUpdateTimeOut.current);

                            if (!refByPassOnValueChangedEvent.current)
                                onDataChanged({
                                    value: e.value,
                                    display: e.value,
                                    previousValue: e.previousValue
                                    // rowData: rowData
                                });
                        }, 100);
            }
        },
        [onDataChanged]
    );

    const onOpened = useCallback(
        (e) => {
            if (props.acceptCustomValue) {
                setTimeout(function () {
                    e.component.focus();
                });
            }

            // if (grid.current && grid.current.instance) {
            //     if (props.refreshOnOpened || (props.gridOptions && props.gridOptions.refreshOnOpened)) grid.current.instance.refresh();
            //     else {
            //         if (dataSource) {
            //             var cachedData = dataSource.getCachedData();
            //             if (cachedData && cachedData instanceof Array && cachedData.length <= 0) grid.current.instance.refresh();
            //         }
            //     }
            // }
        },
        [props.acceptCustomValue]
    );
    // const dropdownDataSource = useRef(initDropDownDataSource());

    // const [loading, setLoading] = useState(false);
    // const [loaded, setLoaded] = useState(false);
    // const [searchBarFocused, setSearchBarFocused] = useState(false);

    const popUpWidth = props.width ? props.width : '100%';
    const popUpHeight = props.height ? props.height : '60%';
    // Kích thước popup người dùng đã kéo (AntGridSettings.popup) — kiểu hiển thị (phủ nền / theo ô) vẫn theo cấu hình.
    const savedPopup = savedSettings.current.popup && typeof savedSettings.current.popup === 'object' ? savedSettings.current.popup : {};

    // Lưới giãn theo chiều cao popup: phần còn lại sau thanh tìm kiếm / nhóm lọc.
    const fitGridToPopup = useCallback((popup) => {
        const instance = grid.current && grid.current.instance;
        if (!popup || !instance) return;
        const contentEl = popup.content ? popup.content() : null;
        const content = contentEl && contentEl.get ? contentEl.get(0) : contentEl;
        const gridRoot = instance.element();
        const gridEl = gridRoot && gridRoot.get ? gridRoot.get(0) : gridRoot;
        if (!content || !gridEl) return;
        const top = gridEl.getBoundingClientRect().top - content.getBoundingClientRect().top;
        const style = window.getComputedStyle(content);
        const bottom = parseFloat(style.paddingBottom) || 0;
        const height = Math.floor(content.clientHeight - top - bottom);
        if (height > 120 && Math.abs((instance.option('height') || 0) - height) >= 1) instance.option('height', height);
    }, []);

    const onPopupResizeEnd = useCallback(
        (e) => {
            const width = e.component.option('width');
            const height = e.component.option('height');
            const popup = {};
            if (typeof width === 'number' && width > 200) popup.width = Math.round(width);
            if (typeof height === 'number' && height > 150) popup.height = Math.round(height);
            if (Object.keys(popup).length) {
                savedSettings.current = { ...savedSettings.current, popup };
                saveSettings((entry) => ({ ...entry, popup }));
            }
            fitGridToPopup(e.component);
        },
        [saveSettings, fitGridToPopup]
    );

    // const showClearButton = !props.showLookupButton && (props.showClearButton !== undefined ? props.showClearButton : true);
    const showClearButton =
        props.acceptCustomValue || (!props.showLookupButton && (props.showClearButton !== undefined ? props.showClearButton : true));
    const showDropDownButton = props.showDropDownButton !== undefined ? props.showDropDownButton : true;
    const showLookupButton = props.showLookupButton || props.dataField === 'code' || props.dataField === 'id';

    return (
        <React.Fragment>
            <DropDownBox
                defaultValue={props.value}
                value={mainValue}
                acceptCustomValue={props.acceptCustomValue}
                dataSource={dropdownDataSource.dataSource}
                valueExpr={dropdownDataSource.valueExpr}
                displayExpr={dropdownDataSource.displayExpr}
                placeholder={props.placeholder ? props.placeholder : '...'}
                {...props.editorOptions}
                name={props.name}
                focusStateEnabled={props.focusStateEnabled !== undefined ? Boolean(props.focusStateEnabled) : true}
                showClearButton={showClearButton}
                showDropDownButton={showDropDownButton}
                dropDownOptions={{
                    width: savedPopup.width || popUpWidth,
                    // height: popUpHeight,
                    ...(savedPopup.height ? { height: savedPopup.height } : {}),
                    shading: popUpWidth !== '100%',
                    container: popUpWidth !== '100%' ? '#root' : undefined,
                    resizeEnabled: true,
                    wrapperAttr: { class: 'ant-dropdown-topmost', style: 'z-index: 2147483647 !important' },
                    // position: { my: 'left top', at: 'left top', of: popUpWidth !== '100%' ? '#root' : undefined },
                    ...props.dropDownOptions,
                    onResize: (e) => fitGridToPopup(e.component),
                    onResizeEnd: (e) => {
                        onPopupResizeEnd(e);
                        if (props.dropDownOptions && props.dropDownOptions.onResizeEnd) props.dropDownOptions.onResizeEnd(e);
                    },
                    onShown: (e) => {
                        if (savedPopup.height) fitGridToPopup(e.component);
                        if (props.dropDownOptions && props.dropDownOptions.onShown) props.dropDownOptions.onShown(e);
                    }
                }}
                dropDownButtonTemplate={showLookupButton ? 'renderLookupButton' : 'dropDownButton'}
                contentTemplate={'renderContent'}
                rowAlternationEnabled={true}
                loadPanel={{ enabled: false }}
                onOpened={onOpened}
                onInput={(e) => {
                    var opened = e.component.option('opened');
                    if (!opened) e.component.open();
                    if (props.acceptCustomValue) {
                        onSearchText(e.component.option('text'));
                    }
                }}
                onKeyDown={(e) => {
                    let keyCode = e.event.key;
                    if (keyCode === 'Enter' || keyCode === 'Tab') e.component.close();
                }}
                onFocusIn={(e) => {
                    let inputEl = e.element.querySelector('.dx-texteditor-input');
                    if (inputEl) {
                        inputEl.selectionStart = 0;
                        inputEl.selectionEnd = inputEl.value.length;
                    }
                    if (props.onFocusIn)
                        props.onFocusIn({
                            dataField: props.dataField
                        });
                }}
                // onFocusOut={(e) => {
                //     var newValue = e.component.option('value');
                //     if (newValue !== mainValue) setMainValue(newValue);
                // }}
                onValueChanged={onValueChanged}
                onContentReady={onContentReady}
            >
                {props.validationRules && <Validator validationRules={props.validationRules} dataField={props.dataField} />}
                {<Template name={'renderContent'} render={renderContent} />}
                {<Template name={'renderLookupButton'} render={renderLookupButton} />}
            </DropDownBox>
        </React.Fragment>
    );
});
